import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { CacheService, MiningToastService } from 'projects/common/src/app/services';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { LanguageService } from '../../../../../../projects/common/src/app/services/parameters/language/language.service';
import { excelExportDateFormat } from '../../../../../../projects/common/src/app/services/utils/util.service';
import { ICheckFilter } from '../../../core/interfaces/check-filter';
import { IOutputFilter } from '../../../core/interfaces/output-filter';
import { BooleanStateService } from '../../../core/services/boolean-state.service';
import { FilterHandlerService } from '../../../core/services/filter/filter-handler.service';
import { ILayoutConfig } from '../../../layout/interfaces/layout-config';
import { LayoutConfigService } from '../../../layout/services/layout-config.service';
import { ILoopByMachineResponse } from '../../interfaces/report-loop-by-machine';
import { ReportLoopByMachineService } from '../../services/report-loop-by-machine.service';

@Component({
  selector: 'app-loop-by-machine-shell',
  templateUrl: './loop-by-machine-shell.component.html',
  styleUrls: ['./loop-by-machine-shell.component.scss'],
})
export class LoopByMachineShellComponent implements OnInit {
  public showFilter: ICheckFilter;
  public selectedFilters: IOutputFilter;
  public message: string = null;
  public reportResult: ILoopByMachineResponse;
  public canDownload = false;
  public hasData = false;
  public isTruck: boolean;
  public isLhd: boolean;
  public systemError = '';
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  private machineType: IMachineContext;

  constructor(
    protected loopMachineService: ReportLoopByMachineService,
    protected languageService: LanguageService,
    protected layoutConfigService: LayoutConfigService,
    protected progressBarService: BooleanStateService,
    protected filterHandlerService: FilterHandlerService,
    protected cacheService: CacheService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected _excelExportService: ExcelExportService,
    protected _toastService: MiningToastService,
    protected cdr: ChangeDetectorRef,
  ) {
    this.disabledExcelBtn = false;
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    this.isTruck = false;
    this.isLhd = true;
    this.initLayoutTitle();
    this.declareFilters();
    this.cdr.detectChanges();
  }

  /**
   * Function to declare filter to show
   */
  protected declareFilters(): void {
    const allMachines = this.cacheService.session('machineCollection') as IMachineContext[];
    this.machineType = allMachines ? allMachines.find((v) => (this.isTruck ? v.code === 'TRU' : v.code === 'SCOP')) : null;

    this.filterConfig = {
      dates: { required: true },
      shift: { required: true },
      machineType: { required: true, uniqueSelection: this.machineType },
      mine: { required: true },
      machineList: { required: true, multiselect: true, dependsOn: ['mine'] },
      cleanButton: true,
    };
  }

  /**
   * Function to select icon image, title & subtitle
   */
  protected initLayoutTitle(): void {
    const subtitle = this.isLhd ? 'report-loops-by-machine' : 'report-loops-by-machine-truck';
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle,
      imageKey: 'report',
      imageType: 'section',
    };
    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public getReportByMachineData(selectedFilters: IOutputFilter): void {
    this.filterConfig.isLoading = true;
    this.selectedFilters = selectedFilters;

    this.loopMachineService.getLoopByMachineFilterResult(this.selectedFilters, this.isTruck).then(
      (response) => {
        response?.loopsDetailByDate.map((td) => {
          td.machineName = td.machineName || '-';
          td.operator = td.operator || '-';
          td.streetZoneStart = td.streetZoneStart || '-';
          td.startZone = td.startZone || '-';
          td.startedAt = td.startedAt || '-';
          td.streetZoneEnd = td.streetZoneEnd || '-';
          td.endZone = td.endZone || '-';
          td.finishedAt = td.finishedAt || '-';
          td.difference = td.difference || '-';
          td.shiftName = td.shiftName || '-';
          return td;
        });

        this.reportResult = response;
        const thereIsLoopByMachineResult = !!this.reportResult.loopsDetailByDate && this.reportResult.loopsDetailByDate.length > 0;
        const thereIsLoopByMachineTable = !!this.reportResult.totalLoopsByMachine && this.reportResult.totalLoopsByMachine.length > 0;
        const thereIsLoopByShiftCards = !!this.reportResult.totalLoopsByShift && this.reportResult.totalLoopsByShift.length > 0;

        if (thereIsLoopByMachineResult && thereIsLoopByMachineTable && thereIsLoopByShiftCards) {
          this.canDownload = true;
          this.hasData = true;
          this.message = null;
        } else {
          this.hasData = false;
          this.canDownload = false;
          this.message = this.languageService.message('m034');
        }
        this.systemError = '';
        this.filterConfig.isLoading = false;
      },
      ({ name, message }) => {
        console.error(message);
        this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
        this.hasData = false;
        this.canDownload = false;
        this.filterConfig.isLoading = false;
      },
    );
    const filters = {
      reports: 'loop-by-machine',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to download report in excel format
   */
  public async downloadExcel(): Promise<void> {
    const selectRangeDate = {
      startDate: excelExportDateFormat(this.selectedFilters.startDate),
      endDate: excelExportDateFormat(this.selectedFilters.endDate),
    };
    const nameFile = this.machineType.code === 'TRU' ? 'report-loops-by-machine-truck' : 'shelves-machine-report-excel-filename';
    this.btnSpinner = true;
    this.disabledExcelBtn = true;

    this.loopMachineService.GetLoopByMachineReportExcel(this.selectedFilters, this.isTruck).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, nameFile, selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.log(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
